import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface AppSetting {
  key: string;
  value: any;
  type: 'string' | 'number' | 'boolean';
  label: string;
  description?: string;
  category: 'software_billing' | 'invoice' | 'hospital' | 'print' | 'general';
}

const SYSTEM_SETTINGS: AppSetting[] = [
  { key: 'currency_code', value: 'BDT', type: 'string', label: 'Currency Code', description: 'Currency code (BDT, USD, EUR, etc.)', category: 'general' },
  { key: 'currency_symbol', value: '৳', type: 'string', label: 'Currency Symbol', description: 'Currency symbol to display (৳, $, €, etc.)', category: 'general' },
  
  { key: 'bkash_send_money_number', value: '01681354066', type: 'string', label: 'bKash Send Money Number', description: 'bKash merchant number for payments', category: 'software_billing' },
  { key: 'subscription_monthly_fee', value: 1000, type: 'number', label: 'Monthly Subscription Fee', description: 'Monthly subscription fee in BDT', category: 'software_billing' },
  { key: 'bkash_charge_rate', value: 0.015, type: 'number', label: 'bKash Charge Rate', description: 'bKash transaction charge rate (e.g., 0.015 for 1.5%)', category: 'software_billing' },
  { key: 'sms_min_qty', value: 1000, type: 'number', label: 'SMS Minimum Quantity', description: 'Minimum SMS quantity for purchase', category: 'software_billing' },
  { key: 'sms_unit_price', value: 0.30, type: 'number', label: 'SMS Unit Price', description: 'Price per SMS in BDT', category: 'software_billing' },
  
  { key: 'invoice_prefix', value: 'INV', type: 'string', label: 'Invoice Prefix', description: 'Prefix for invoice numbers', category: 'invoice' },
  { key: 'invoice_note', value: 'Thank you for your business', type: 'string', label: 'Default Invoice Note', description: 'Default note shown on invoices', category: 'invoice' },
  
  { key: 'hospital_name_en', value: 'Hospital Management System', type: 'string', label: 'Hospital Name (English)', description: 'Hospital name in English', category: 'hospital' },
  { key: 'hospital_name_bn', value: 'হাসপাতাল ব্যবস্থাপনা সিস্টেম', type: 'string', label: 'Hospital Name (Bangla)', description: 'Hospital name in Bangla', category: 'hospital' },
  { key: 'hospital_address', value: '', type: 'string', label: 'Hospital Address', description: 'Full hospital address', category: 'hospital' },
  { key: 'hospital_phone', value: '', type: 'string', label: 'Hospital Phone', description: 'Hospital contact phone number', category: 'hospital' },
  { key: 'hospital_email', value: '', type: 'string', label: 'Hospital Email', description: 'Hospital contact email', category: 'hospital' },
  { key: 'hospital_website', value: '', type: 'string', label: 'Hospital Website', description: 'Hospital website URL', category: 'hospital' },
  
  { key: 'print_show_qr', value: true, type: 'boolean', label: 'Show QR Code on Prints', description: 'Display QR code on printed invoices', category: 'print' },
  { key: 'print_show_watermark', value: true, type: 'boolean', label: 'Show Watermark on Prints', description: 'Display watermark on printed invoices', category: 'print' },
  { key: 'print_default_template', value: 'standard', type: 'string', label: 'Default Print Template', description: 'Default invoice print template (standard/compact/customer)', category: 'print' },
];

export default function SystemSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [activeCategory, setActiveCategory] = useState<string>('general');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('app_settings')
        .select('*');

      if (error) throw error;

      const settingsMap: Record<string, any> = {};
      
      data?.forEach(setting => {
        settingsMap[setting.key] = setting.value;
      });

      SYSTEM_SETTINGS.forEach(setting => {
        if (!(setting.key in settingsMap)) {
          settingsMap[setting.key] = setting.value;
        }
      });

      setSettings(settingsMap);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load system settings');
      
      const defaultSettings: Record<string, any> = {};
      SYSTEM_SETTINGS.forEach(setting => {
        defaultSettings[setting.key] = setting.value;
      });
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const settingsToUpsert = Object.entries(settings).map(([key, value]) => ({
        key,
        value,
      }));

      const { error } = await supabase
        .from('app_settings')
        .upsert(settingsToUpsert, { onConflict: 'key' });

      if (error) throw error;

      toast.success('System settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save system settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key: string, value: any, type: 'string' | 'number' | 'boolean') => {
    let parsedValue = value;
    
    if (type === 'number') {
      parsedValue = parseFloat(value) || 0;
    } else if (type === 'boolean') {
      parsedValue = value === 'true' || value === true;
    }

    setSettings(prev => ({
      ...prev,
      [key]: parsedValue,
    }));
  };

  const categories = [
    { id: 'general', name: 'General Settings', icon: '⚙️' },
    { id: 'software_billing', name: 'Software Billing', icon: '💳' },
    { id: 'invoice', name: 'Invoice Settings', icon: '📄' },
    { id: 'hospital', name: 'Hospital Branding', icon: '🏥' },
    { id: 'print', name: 'Print Settings', icon: '🖨️' },
  ];

  const filteredSettings = SYSTEM_SETTINGS.filter(s => s.category === activeCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading system settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">System Settings</h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure system-wide settings for your hospital management system
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400 transition-colors"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      {/* Category Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-4">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeCategory === category.id
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{category.icon}</span>
              <span>{category.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Settings Form */}
      <div className="space-y-4">
        {filteredSettings.map(setting => (
          <div key={setting.key} className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  {setting.label}
                </label>
                {setting.description && (
                  <p className="text-xs text-gray-500 mb-2">{setting.description}</p>
                )}
                
                {setting.type === 'boolean' ? (
                  <select
                    value={String(settings[setting.key] ?? setting.value)}
                    onChange={(e) => handleChange(setting.key, e.target.value, setting.type)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : setting.key === 'print_default_template' ? (
                  <select
                    value={String(settings[setting.key] ?? setting.value)}
                    onChange={(e) => handleChange(setting.key, e.target.value, setting.type)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="standard">Standard</option>
                    <option value="compact">Compact</option>
                    <option value="customer">Customer Copy</option>
                  </select>
                ) : setting.key === 'currency_code' ? (
                  <select
                    value={String(settings[setting.key] ?? setting.value)}
                    onChange={(e) => handleChange(setting.key, e.target.value, setting.type)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="BDT">BDT - Bangladeshi Taka (৳)</option>
                    <option value="USD">USD - US Dollar ($)</option>
                    <option value="EUR">EUR - Euro (€)</option>
                    <option value="GBP">GBP - British Pound (£)</option>
                    <option value="INR">INR - Indian Rupee (₹)</option>
                    <option value="JPY">JPY - Japanese Yen (¥)</option>
                    <option value="CNY">CNY - Chinese Yuan (¥)</option>
                    <option value="AED">AED - UAE Dirham (د.إ)</option>
                    <option value="SAR">SAR - Saudi Riyal (ر.س)</option>
                  </select>
                ) : (
                  <input
                    type={setting.type === 'number' ? 'number' : 'text'}
                    value={settings[setting.key] ?? setting.value}
                    onChange={(e) => handleChange(setting.key, e.target.value, setting.type)}
                    step={setting.type === 'number' ? 'any' : undefined}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Important Notes:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Changes to system settings will affect all users and modules</li>
            <li>bKash settings are used by the Software Billing module</li>
            <li>Invoice settings apply to all generated invoices</li>
            <li>Print settings control the appearance of printed documents</li>
            <li>Make sure to click "Save Changes" after modifying any settings</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
